import React, { useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ClassicEditor, { EMAIL_EDITOR_TOOLBAR } from './customCkEditor';

// Default only — callers should pass placeholderTokens explicitly (EmailTemplatesSettings.js
// does, sourced from its own single PLACEHOLDER_TOKENS list) so the "Insert placeholder"
// dropdown never drifts out of sync with the "Available placeholders" banner again. This
// exact drift was a real bug found in a previous pass: {tracking_link} was shown in the
// banner but missing here, so it wasn't actually selectable from the dropdown.
const DEFAULT_PLACEHOLDER_TOKENS = ['{customer_name}', '{order_number}', '{items}', '{delivery_date}'];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB — same ceiling as the logo/signature AssetUploader

// No upload adapter is configured by CKEditor out of the box — without one, the
// "uploadImage" toolbar button opens the file picker but the upload never completes
// (FileRepository has nothing to hand the file to). This adapter reads the file directly
// to a base64 data URL via FileReader, matching the same upload pattern already used
// everywhere else in this app (AssetUploader in EmailBrandingSection.js, BusinessProfile
// logo) — no new backend endpoint, no filesystem storage, no new npm dependency.
class Base64UploadAdapter {
    constructor(loader, onError) {
        this.loader = loader;
        this.onError = onError;
    }

    upload() {
        return this.loader.file.then((file) => new Promise((resolve, reject) => {
            if (!file.type.startsWith('image/')) {
                const msg = this.onError?.('invalidType') || 'Invalid image type';
                reject(msg);
                return;
            }
            if (file.size > MAX_IMAGE_BYTES) {
                const msg = this.onError?.('tooLarge') || 'Image too large';
                reject(msg);
                return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve({ default: reader.result });
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        }));
    }

    abort() {
        // Nothing to cancel — FileReader reads are effectively instant for the size cap above.
    }
}

export default function RichTextEditor({ value, onChange, showPlaceholders = true, placeholderTokens = DEFAULT_PLACEHOLDER_TOKENS }) {
    const { t } = useTranslation();
    const editorRef = useRef(null);
    const [ready, setReady] = useState(false);

    const insertPlaceholder = (token) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.model.change((writer) => {
            const insertPosition = editor.model.document.selection.getFirstPosition();
            writer.insertText(token, insertPosition);
        });
        editor.editing.view.focus();
    };

    return (
        <div className="email-template-rich-text-editor">
            {showPlaceholders && (
                <div className="d-flex justify-content-end mb-2">
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!ready}>
                            <i className="fa-solid fa-code me-1" /> {t('emailTemplates.insertPlaceholder')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {placeholderTokens.map((token) => (
                                <Dropdown.Item key={token} onClick={() => insertPlaceholder(token)}>
                                    <code>{token}</code>
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            )}
            <CKEditor
                editor={ClassicEditor}
                config={{ toolbar: EMAIL_EDITOR_TOOLBAR }}
                data={value || ''}
                onReady={(editor) => {
                    editorRef.current = editor;
                    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new Base64UploadAdapter(loader, (kind) => {
                        const msg = kind === 'tooLarge'
                            ? t('emailTemplates.branding.errors.imageTooLarge')
                            : t('emailTemplates.branding.invalidType');
                        toast.error(msg);
                        return msg;
                    });
                    setReady(true);
                }}
                onChange={(event, editor) => {
                    onChange(editor.getData());
                }}
            />
        </div>
    );
}
